<template>
  <div v-if="isLoaded">
    <div class="finance-santander widget">
      <div
        class="spinner__loader-container spinner__loader-container--transparent"
        v-if="showSpinner"
      >
        <div class="spinner--loader"></div>
      </div>
      <div class="widget__content-container">
        <div class="widget__header">Finansiering</div>
        <p class="widget__subtitle">
          Har du fundet din drømmebil? Se her hvordan, du kan finansiere den.
          Prøv låneberegneren, og få et uforpligtende lånetilbud fra Santander.
        </p>
      </div>
      <div class="finance-santander__inner widget__content-container">
        <div
          class="finance-santander__inner-child finance-santander__inner-child--large"
        >
          <div class="finance-santander__details-title">
            Billån pr. md.
            {{ thousandSeparator(payment.PaymentPerMonth) }}
            kr.
          </div>
          <img
            loading="lazy"
            src="@assets/icons/santander-logo.svg"
            alt="Santander logo"
          />
          <div
            v-html="payment.DefaultDutyText"
            class="finance-santander__duty-text"
          ></div>
          <div class="finance-santander__finance-title">
            Tilpas finansieringen
          </div>
          <div class="sliders">
            <SantanderSlider
              v-model="paymentCash"
              title="Udbetaling"
              postfix="kr."
              @drag-end="calculate"
              :min="calculation.MinDownPayment"
              :max="calculation.MaxDownPayment"
              :interval="1000"
            ></SantanderSlider>
            <SantanderSlider
              v-model="months"
              title="Løbetid"
              postfix="måned(er)"
              @drag-end="calculate"
              :min="calculation.MinPaymentTerms"
              :max="calculation.MaxPaymentTerms"
            ></SantanderSlider>
          </div>
        </div>
        <div
          class="finance-santander__inner-child finance-santander__inner-child--small"
        >
          <div class="finance-santander__details">
            <div
              class="finance-santander__details-title finance-santander__details-title--hide-large"
            >
              Billån pr. md.
              {{ thousandSeparator(payment.PaymentPerMonth) }}
              kr.
            </div>
            <dl class="finance-santander__details-table table-flex">
              <dt class="table-flex__type">Samlet kreditbeløb</dt>
              <dd class="table-flex__definition">
                {{ thousandSeparator(payment.BaseAmount) }} kr.
              </dd>
              <dt class="table-flex__type">
                Debitorrente {{ payment.InterestType }}
              </dt>
              <dd class="table-flex__definition">
                {{ formatComma(payment.NominalInterest) }} %
              </dd>
              <dt class="table-flex__type">ÅOP</dt>
              <dd class="table-flex__definition">
                {{ formatComma(payment.AOPBeforeTax) }} %
              </dd>
              <dt class="table-flex__type">Samlede omkostninger</dt>
              <dd class="table-flex__definition">
                {{ thousandSeparator(payment.TotalLoanCosts) }} kr.
              </dd>
              <dt class="table-flex__type">Samlet tilbagebetaling</dt>
              <dd class="table-flex__definition">
                {{ thousandSeparator(payment.TotalRepay) }} kr.
              </dd>
            </dl>
            <button
              class="finance-santander__button button button--highlight"
              @click="createApplication"
            >
              Ansøg nu
            </button>
          </div>
        </div>
      </div>
    </div>
    <img
      v-if="showPixel"
      :src="santanderTrackingPixel"
      height="1"
      width="1"
      alt="Advertisement"
    />
  </div>
</template>

<script>
import { app } from '@/main'
// On Santander we import
import axios from 'axios'
import SantanderSlider from './SantanderSlider.vue'
import {
  LogWidgetShownFinancing,
  LogWidgetCreateApplicationFinancing,
  LogWidgetCalculationFinancing,
} from '@/events'
import { thousandSeparator } from '@/functions'
import { logErrorSantander } from '@/errorHandler'

const partnerId = '1' // 1 is defined to be Santander's Partner Id

export default {
  name: 'FinanceSantander',
  components: {
    SantanderSlider,
  },
  props: {
    partnerExternalDealerId: { default: null, type: String },
    flow: { default: null, type: String },
    publicApiKey: { default: null, type: String },
    make: { default: null, type: String },
    model: { default: null, type: String },
    variant: { default: null, type: String },
    mileAge: { default: null, type: String },
    firstRegistrationDate: { default: null, type: String },
    objectPrice: { default: 0, type: Number },
    adId: { default: '', type: String },
    companyId: { default: '', type: String },
    santanderTrackingPixel: { default: null, type: String },
    santanderApplyNowQuerystrings: { default: '', type: String },
  },
  data() {
    return {
      isLoaded: false,
      showSpinner: false,
      showPixel: false,
      paymentCash: 0,
      months: 96, // defined by Santander
      payment: {
        FinanceValue: null,
        BaseAmount: null,
        InterestType: null,
        NominalInterest: null,
        AOPBeforeTax: null,
        TotalLoanCosts: null,
        TotalRepay: null,
        DefaultDutyText: null,
        PaymentTerms: null,
        DownPayment: null,
        ModelYear: null,
        Url: null,
        ShowAsPriceLabel: null,
        Agreement: null,
        CorrelationId: null,
        AccessToken: null,
      },
      calculation: {
        MaxDownPayment: 50000,
        MinDownPayment: 0, // defined by Santander
        MaxPaymentTerms: 96, // defined by Santander
        MinPaymentTerms: 12, // defined by Santander
      },
    }
  },
  methods: {
    thousandSeparator(value) {
      return thousandSeparator(value)
    },
    formatComma(str) {
      return str.toString().replace(',', '.')
    },
    ensureDivisableBy1000(value) {
      return Math.floor(value / 1000.0) * 1000
    },
    calculateDefaultCash() {
      let valueDefault = this.ensureDivisableBy1000(this.objectPrice * 0.2)
      if (valueDefault > 0) {
        valueDefault = valueDefault + 1000 //bump the downpayment value with 1000 to ensure that it is at least 20% due to ensureDivisableBy1000 flooring the value. Example: 429.900, 20% = 85.980. ensureDivisableBy1000 = 85.000 (and thereby below 20%). Bump with 1000 = 86.000 and above 20%
      }
      if (valueDefault < this.calculation.MinDownPayment) {
        valueDefault = this.calculation.MinDownPayment
      }

      const minDownPayment = this.ensureDivisableBy1000(
        this.objectPrice - this.calculation.MaxDownPayment
      )
      if (valueDefault > minDownPayment) {
        valueDefault = minDownPayment
      }

      this.paymentCash = valueDefault
    },
    init() {
      const minLend = 50000

      if (this.objectPrice < minLend) {
        // price too low don't show Santander
        return
      }

      this.calculation.MaxDownPayment = this.ensureDivisableBy1000(
        this.objectPrice - minLend
      ) // defined by Santander

      this.calculate()
    },
    calculate() {
      this.showSpinner = true

      axios
        .post(
          'https://api.scb.nu/SCBDK.Dealer.ExternalCalc/v2/api/Calculator',
          this.getSantanderObject()
        )
        .then(resp => {
          if (resp.data.ErrorMessage) {
            let santanderError = {
              adId: this.adId,
              companyId: this.companyId,
              flowId: this.flow,
              errorMessage: resp.data.ErrorMessage,
              response: resp.data,
            }
            logErrorSantander(santanderError)
            return
          }

          this.showSpinner = false

          // ensure data doesn't hit outer bounds

          this.payment = resp.data

          if (this.isLoaded) {
            LogWidgetCalculationFinancing()
          } else {
            this.isLoaded = true
            setTimeout(() => {
              app.mitt.emit('finance-module-loaded')
            }, 250)

            LogWidgetShownFinancing(this.adId, partnerId)
          }
        })
        .catch(() => {
          this.isLoaded = false
        })
    },
    createApplication() {
      const newWin = window.open('/FinansieringPaaFemMinutter', '_blank')
      if (newWin === null) return

      axios
        .post(
          'https://api.scb.nu/SCBDK.Dealer.ExternalCalc/v2/api/Application',
          {
            CorrelationId: this.payment.CorrelationId,
            ...this.getSantanderObject(),
          }
        )
        .then(resp => {
          newWin.location.href =
            resp.data.RedirectUrl + this.santanderApplyNowQuerystrings
          LogWidgetCreateApplicationFinancing(this.adId, partnerId)
        })
    },
    getSantanderObject() {
      return {
        DownPayment: this.paymentCash,
        FirstRegistrationDate: this.firstRegistrationDate,
        Flow: this.flow,
        Make: this.make,
        MileAge: this.mileAge,
        Model: this.model,
        ObjectPrice: this.objectPrice,
        PartnerExternalDealerId: this.partnerExternalDealerId,
        PaymentTerms: this.months,
        PublicApiKey: this.publicApiKey,
        Variant: this.variant,
      }
    },
  },
  created() {
    this.calculateDefaultCash()

    this.init()
  },
  mounted() {
    // if cookie accepted
    if (window.CookieInformation.getConsentGivenFor('cookie_cat_marketing')) {
      this.showPixel = true
    }
  },
}
</script>

<style lang="scss">
@import 'FinanceSantander.scss';
</style>
